import React, { useContext, useState } from 'react';
import { XIcon } from 'lucide-react';
import { toast } from 'sonner';

import { CycleCountApi } from 'api/cycleCount';
import { UserContext } from 'contexts/UserContext';
import { Badge } from 'shadcn/components/ui/Badge';
import { Button } from 'shadcn/components/ui/Button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from 'shadcn/components/ui/Dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'shadcn/components/ui/Select';

import { PartnerCycleCountConflictModal } from './partnerCycleCountConflictModal';

const cycleCountApi = new CycleCountApi();

function PartnerCycleCountForm({ closeModal, onSuccess, open, sourceOptions = [] }) {
    const [selectedSources, setSelectedSources] = useState([]);
    const [currentSelection, setCurrentSelection] = useState('');
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showConflictModal, setShowConflictModal] = useState(false);
    const [conflictData, setConflictData] = useState(null);
    const [isOverriding, setIsOverriding] = useState(false);
    const { user } = useContext(UserContext);

    const handleCreate = async (overrideExisting = false) => {
        if (isSubmitting && !overrideExisting) {
            return;
        }
        if (selectedSources.length === 0) {
            setError('Please select at least one company source');
            return;
        }
        setError('');

        if (!overrideExisting) {
            setIsSubmitting(true);
        }

        const res = await cycleCountApi.createPartnerCycleCountRequest(
            user.facilityID,
            selectedSources,
            overrideExisting,
        );

        if (!res.status) {
            // Check if this is a conflict that requires confirmation
            if (res.data?.requiresConfirmation === true) {
                setConflictData(res.data);
                setShowConflictModal(true);
                setIsSubmitting(false);
                return;
            }

            // Other errors
            setError(res.msg || 'Failed to create cycle count request');
            setIsSubmitting(false);
            setIsOverriding(false);
            return;
        }

        // Success
        toast.success('Partner cycle count created successfully');
        handleClose();
        onSuccess();
    };

    const handleConfirmOverride = async () => {
        setIsOverriding(true);
        const res = await cycleCountApi.createPartnerCycleCountRequest(
            user.facilityID,
            selectedSources,
            true, // overrideExisting = true
        );

        setIsOverriding(false);

        if (!res.status) {
            // Close conflict modal and show error in form
            handleCloseConflictModal();
            setError(res.msg || 'Failed to create cycle count request');
            return;
        }

        // Success - close both modals and show success
        toast.success('Partner cycle count created successfully');
        handleClose();
        onSuccess();
    };

    const handleCloseConflictModal = () => {
        setShowConflictModal(false);
        setConflictData(null);
        setIsOverriding(false);
    };

    const handleClose = () => {
        setSelectedSources([]);
        setCurrentSelection('');
        setIsSubmitting(false);
        setError('');
        setShowConflictModal(false);
        setConflictData(null);
        setIsOverriding(false);
        closeModal();
    };

    const handleAddSource = (value) => {
        if (value && !selectedSources.includes(value)) {
            setSelectedSources([...selectedSources, value]);
            setCurrentSelection('');
            setError('');
        }
    };

    const handleRemoveSource = (value) => {
        setSelectedSources(selectedSources.filter((source) => source !== value));
    };

    const getSourceLabel = (value) => sourceOptions.find((option) => option.value === value)?.label || value;

    const availableOptions = sourceOptions.filter((option) => !selectedSources.includes(option.value));

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Create New Partner Cycle Count</DialogTitle>
                    <DialogDescription>Add one or more company sources to create a new request.</DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                    <div className="space-y-3">
                        <Select onValueChange={handleAddSource} value={currentSelection}>
                            <SelectTrigger>
                                <SelectValue placeholder="Add a company source" />
                            </SelectTrigger>
                            <SelectContent>
                                {availableOptions.map((option) => (
                                    <SelectItem key={option.value} value={option.value}>
                                        {option.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {selectedSources.length > 0 && (
                            <div className="space-y-2">
                                <div className="text-sm font-medium">Selected Sources:</div>
                                <div className="flex flex-wrap gap-2">
                                    {selectedSources.map((source) => (
                                        <Badge
                                            key={source}
                                            variant="secondary"
                                            className="flex items-center gap-1 pr-1"
                                        >
                                            <span>{getSourceLabel(source)}</span>
                                            <button
                                                type="button"
                                                className="ml-1 rounded-full hover:bg-destructive/20 p-0.5"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    handleRemoveSource(source);
                                                }}
                                            >
                                                <XIcon className="h-3 w-3 hover:text-destructive" />
                                            </button>
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                {error && <div className="text-sm text-destructive">{error}</div>}
                <DialogFooter>
                    <Button variant="outline" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button onClick={handleCreate} disabled={isSubmitting}>
                        {isSubmitting ? 'Creating...' : 'Create'}
                    </Button>
                </DialogFooter>
            </DialogContent>

            <PartnerCycleCountConflictModal
                open={showConflictModal}
                onClose={handleCloseConflictModal}
                onConfirm={handleConfirmOverride}
                conflictData={conflictData}
                sourceOptions={sourceOptions}
                isLoading={isOverriding}
            />
        </Dialog>
    );
}

export { PartnerCycleCountForm };
